import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { IPersonalSalud, IPopulate, IQueryParam } from 'modelos/src';
import { Subscription } from 'rxjs';
import { AuxiliaresModule } from '../../../../../../auxiliares/auxiliares.module';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../../../auxiliares/tabla/tabla.component';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../../../auxiliares/tabla/filtro/filtro.component';
import { DialogService } from '../../../../../../auxiliares/dialog/dialog.service';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../../../auxiliares/servicios/lista2.service';
import { PersonalesSaludService } from '../../../../../../auxiliares/servicios/http/personales-salud.service';
import { ClienteJerarquiaService } from '../../../../../../auxiliares/servicios/cliente-jerarquia.service';
import {
  esc,
  filtroActivo,
  estadoActivo,
  htmlContacto,
  htmlEntidad,
  iconoRolSalud,
  sinDato,
} from '../../presentacion-entidades';
import { CrearEditarPersonalSaludComponent } from '../crear-editar-personal-salud/crear-editar-personal-salud.component';
import { FormularioDialogService } from '../../../../../../auxiliares/servicios/formulario-dialog.service';

const RUTA = 'main/emergencias/personalesSalud';

@Component({
  selector: 'app-listado-personales-salud',
  imports: [AuxiliaresModule],
  templateUrl: './listado-personales-salud.component.html',
})
export class PersonalesSaludComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  private lista2 = inject(Lista2Service);
  private dialogService = inject(DialogService);
  private service = inject(PersonalesSaludService);
  private router = inject(Router);
  private formularios = inject(FormularioDialogService);
  private clienteJerarquia = inject(ClienteJerarquiaService);

  public name = 'PersonalesSaludComponent';
  public datos?: IPersonalSalud[];
  public totalCount = 0;
  public columnas?: IColumnas<IPersonalSalud>[];
  private datos$?: Subscription;

  public botonCrear: ICrearBoton = {
    mostrar: this.helper.tienePermiso(
      'Emergencias Médicas - Crear personal de salud',
    ),
    tooltip: 'Crear personal de salud',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.crear(),
  };

  public search: IRegExpSearch = {
    fields: ['nombre', 'dni', 'matricula', 'telefono', 'email'],
  };

  private cliente: IFiltroTabla = {
    filter: { field: 'idCliente' },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // El cliente elegido y toda su descendencia.
  private ancestro: IFiltroTabla = {
    filter: { field: 'idsAncestros', incluirPropio: true },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  private rol: IFiltroTabla = {
    elementos: [
      {
        label: 'Médico',
        icon: iconoRolSalud('Médico'),
        value: 'Médico',
      },
      {
        label: 'Enfermero',
        icon: iconoRolSalud('Enfermero'),
        value: 'Enfermero',
      },
    ],
    filter: { field: 'rol' },
    label: 'Rol',
    tipo: 'select',
    selectIcon: true,
    selectLabel: 'label',
    selectValue: 'value',
  };
  public filtros: IFiltroTabla[] = [this.rol, filtroActivo('activo')];

  private populate: IPopulate[] = [{ path: 'cliente' }];
  public query: IQueryParam = {
    filter: JSON.stringify({}),
    populate: JSON.stringify(this.populate),
    sort: 'nombre',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
    childrenLevel: 0,
  };

  public listar(): void {
    this.datos$?.unsubscribe();
    this.datos$ = this.lista2
      .personalSaluds(this.query, 30)
      .subscribe((data) => {
        if (HelperService.cambiaronLosDatos(this.datos, data.datos)) {
          this.datos = data.datos;
        }
        this.totalCount = data.totalCount;
      });
  }

  // Acciones

  public crear(): void {
    this.abrirFormulario();
  }

  public editar(p: IPersonalSalud): void {
    this.abrirFormulario({ id: p._id });
  }

  /** Crear/editar en diálogo; al guardar se refresca el listado. */
  private abrirFormulario(data?: { id?: string }): void {
    const ref = this.formularios.abrir(CrearEditarPersonalSaludComponent, {
      tamano: 'mediano',
      data,
    });
    ref.afterClosed().subscribe((guardado) => {
      if (guardado) this.listar();
    });
  }

  public detalles(p: IPersonalSalud): void {
    this.router.navigate([RUTA, 'detalles', p._id]);
  }

  public async eliminar(p: IPersonalSalud): Promise<void> {
    const ok = await this.dialogService.confirm(
      'Eliminar personal de salud',
      `¿Confirmás eliminar a <strong>${esc(p.nombre)}</strong>${p.rol ? ` (${esc(p.rol.toLowerCase())})` : ''}?`,
    );
    if (!ok) return;
    try {
      await this.service.delete(p._id!);
      this.helper.notifSuccess('Personal de salud eliminado');
      this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  // Tabla

  private setColumnas(): void {
    const columnas: IColumnas<IPersonalSalud>[] = [
      {
        header: { label: 'Profesional', sort: 'nombre', sortable: true },
        row: {
          html: (d) =>
            htmlEntidad(iconoRolSalud(d.rol), d.nombre, [
              [
                d.rol ? esc(d.rol) : null,
                d.matricula ? `Mat. ${esc(d.matricula)}` : null,
              ]
                .filter(Boolean)
                .join(' · ') || null,
            ]),
        },
      },
      {
        header: { label: 'DNI', sort: 'dni', sortable: true },
        row: { html: (d) => (d.dni ? esc(d.dni) : sinDato('Sin DNI')) },
      },
      {
        header: { label: 'Estado', sort: 'activo', sortable: true },
        row: { estado: (d) => estadoActivo(d.activo) },
      },
      {
        header: { label: 'Contacto' },
        row: { html: (d) => htmlContacto(d.telefono, d.email) },
      },
      {
        header: {
          label: 'Fecha de alta',
          sort: 'fechaCreacion',
          sortable: true,
          hidden: true,
        },
        row: { fecha: (d) => d.fechaCreacion, noData: 'Sin fecha' },
      },
    ];

    if (HelperService.getTipoCliente() !== 'Final') {
      columnas.push({
        header: { label: 'Cliente', hidden: true },
        row: {
          html: (d) => this.clienteJerarquia.htmlNombre(d.cliente),
          tooltip: (d) => this.clienteJerarquia.tooltipJerarquia(d.cliente),
          noData: 'Sin cliente',
        },
      });
    }

    columnas.push({
      header: { label: 'Acciones' },
      row: {
        // Menú: Ver detalle primero, Eliminar al final
        accionesGrouped: [
          {
            tipo: 'material',
            icon: 'visibility',
            tooltip: 'Ver detalle',
            click: (d) => this.detalles(d),
          },
          {
            tipo: 'material',
            color: 'primary',
            icon: 'edit',
            tooltip: 'Editar',
            click: (d) => this.editar(d),
            disabled: () =>
              !this.helper.tienePermiso(
                'Emergencias Médicas - Editar personal de salud',
              ),
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (d) => this.eliminar(d),
            disabled: () =>
              !this.helper.tienePermiso(
                'Emergencias Médicas - Eliminar personal de salud',
              ),
          },
        ],
      },
    });

    this.columnas = columnas;
  }

  // Hooks

  ngOnInit(): void {
    if (HelperService.getTipoCliente() !== 'Final') {
      this.filtros.unshift(this.cliente, this.ancestro);
    }
    this.setColumnas();
    this.listar();
  }

  ngOnDestroy(): void {
    this.datos$?.unsubscribe();
  }
}
